import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { useAppStore } from "../store/useAppStore";
import { toast } from "sonner";
import { Pencil } from "lucide-react";
import { Lead, LeadStatus } from "../types";

export function EditLeadDialog({ lead }: { lead: Lead }) {
  const { updateLead } = useAppStore();
  const [open, setOpen] = useState(false);

  const [sellerName, setSellerName] = useState(lead.sellerName);
  const [phone, setPhone] = useState(lead.phone);
  const [propertyAddress, setPropertyAddress] = useState(lead.propertyAddress);
  const [askingPrice, setAskingPrice] = useState<string>(lead.askingPrice?.toString() || "");
  const [status, setStatus] = useState<LeadStatus>(lead.status);
  const [motivation, setMotivation] = useState(lead.motivation || "");
  const [notes, setNotes] = useState(lead.notes || "");
  const [tags, setTags] = useState<string[]>(lead.tags || []);

  const availableTags = [
    "Pre-Foreclosure",
    "Tax Delinquent",
    "Vacant",
    "Tired Landlord",
    "Probate",
    "Absentee Owner",
    "Code Violation",
    "High Equity",
    "Needs Follow-Up",
    "Needs Skiptrace",
  ];

  const handleTagToggle = (tag: string) => {
    setTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));
  };

  // Update local state if lead changes
  useEffect(() => {
    setSellerName(lead.sellerName);
    setPhone(lead.phone);
    setPropertyAddress(lead.propertyAddress);
    setAskingPrice(lead.askingPrice?.toString() || "");
    setStatus(lead.status);
    setMotivation(lead.motivation || "");
    setNotes(lead.notes || "");
    setTags(lead.tags || []);
  }, [lead]);

  const resetForm = () => {
    setSellerName(lead.sellerName);
    setPhone(lead.phone);
    setPropertyAddress(lead.propertyAddress);
    setAskingPrice(lead.askingPrice?.toString() || "");
    setStatus(lead.status);
    setMotivation(lead.motivation || "");
    setNotes(lead.notes || "");
    setTags(lead.tags || []);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!sellerName || !phone || !propertyAddress) {
      toast.error("Please fill in all required fields.");
      return;
    }

    updateLead(lead.id, {
      sellerName,
      phone,
      propertyAddress,
      askingPrice: askingPrice ? Number(askingPrice) : null,
      status,
      motivation,
      notes,
      tags,
    });

    setOpen(false);
    toast.success("Lead updated successfully!");
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => {
      setOpen(nextOpen);
      if (!nextOpen) resetForm();
    }}>
      <DialogTrigger>
        <div className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-input bg-background text-sm font-medium shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 hover:bg-slate-100 hover:text-accent-foreground">
          <Pencil className="h-4 w-4" />
        </div>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[640px] max-h-[calc(100dvh-1rem)] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Lead</DialogTitle>
          <DialogDescription>
            Update contact details, pipeline status, and notes without losing context.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-5 pt-4">
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
            Required fields are marked with *.
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="sellerName">Seller Name *</Label>
              <Input
                id="sellerName"
                value={sellerName}
                onChange={(e) => setSellerName(e.target.value)}
                placeholder="John Doe"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="phone">Phone Number *</Label>
              <Input
                id="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(555) 123-4567"
              />
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="propertyAddress">Property Address *</Label>
            <Input
              id="propertyAddress"
              value={propertyAddress}
              onChange={(e) => setPropertyAddress(e.target.value)}
              placeholder="123 Main St, City, ST 12345"
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="status">Status</Label>
              <Select value={status} onValueChange={(val) => setStatus(val as LeadStatus)}>
                <SelectTrigger>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="New Lead">New Lead</SelectItem>
                  <SelectItem value="Contacted">Contacted</SelectItem>
                  <SelectItem value="Follow-Up">Follow-Up</SelectItem>
                  <SelectItem value="Appointment Set">Appointment Set</SelectItem>
                  <SelectItem value="Offer Made">Offer Made</SelectItem>
                  <SelectItem value="Under Contract">Under Contract</SelectItem>
                  <SelectItem value="Dead Lead">Dead Lead</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="askingPrice">Asking Price ($)</Label>
              <Input
                id="askingPrice"
                type="number"
                value={askingPrice}
                onChange={(e) => setAskingPrice(e.target.value)}
                placeholder="250000"
              />
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="motivation">Motivation</Label>
            <Input
              id="motivation"
              value={motivation}
              onChange={(e) => setMotivation(e.target.value)}
              placeholder="Why are they selling?"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add call notes, next steps, or other context."
              rows={4}
            />
          </div>

          <div className="grid gap-2">
            <div className="flex items-center justify-between gap-3">
              <Label>Distress Tags</Label>
              <span className="text-xs text-slate-500">{tags.length} selected</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {availableTags.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => handleTagToggle(tag)}
                  className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${
                    tags.includes(tag)
                      ? "border-blue-200 bg-blue-100 font-medium text-blue-700"
                      : "border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit">Save Changes</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
